import React, {
  useCallback,
  forwardRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
// import { useLocation } from 'react-router-dom';
// import { flushSync } from 'react-dom';
import { SkeletonLoader } from '../';
import { Checkbox } from '../';
import {
  CustomFilterControl,
  DataTableRow,
  DataTableToolbar,
  DataTablePagination,
} from './components';
import { useChildFilter, useHierarchialData, useLazyChildren } from './hooks';
import { formatUserFriendly, pluralize } from '../../helpers';
import {
  useFilterableData,
  useSortableData,
  compareItems,
  useQueryParams,
  useDebounce,
  useTooltip,
  useGlobalState,
} from '../../hooks';

import './DataTable.css';

/**
 * Name of the built-in filter control that reveals rows the source system hides, and with it the
 * key its setting is remembered under. One key for every table, so the choice carries across the
 * application the way the view mode and the details pane do.
 */
const SHOW_HIDDEN = 'show_hidden';

/**
 * The tab filter option that shows every row, and the value it reports for it.
 */
const TAB_FILTER_ALL = 'all';

/**
 * The key a row is identified by in the selection. The name is part of it because a table can hold
 * rows of different types whose ids collide (an entity and an attribute both numbered 7).
 * @param {object} item Row item
 * @param {string} column Id column the item is keyed on
 * @returns {string} Selection key
 */
const selectionKey = (item, column) => item[column] + (item.object_name || '');

/**
 * Whether a row is one its source system hides from its own users -- a Power BI table or column
 * marked as hidden, say.
 *
 * Decided per row rather than from one configured column name, because a table can hold rows of
 * more than one kind: a dataset group's tree carries datasets (`dataset_is_hidden`) with their
 * fields nested underneath (`field_is_hidden`). Reading a single property would mark and filter
 * whichever kind happened to come first and quietly ignore the other.
 *
 * The suffix is the convention every such column follows, so a new asset type gets the behaviour
 * without anything here changing.
 *
 * @param {object} row - A row of the table's data.
 * @returns {boolean} True when the row is hidden in its source system.
 */
const isRowHiddenAtSource = row => {
  if (!row) return false;

  for (const key in row) {
    if (row[key] === true && key.endsWith('_is_hidden')) return true;
  }

  return false;
};

/**
 *
 * Making the table expandable:
 * There are two "modes" to make the DataTable display hierarchical data with expandable columns;
 * 1. Child rows:
 *    "data" should be a flattened hierarchy on the form [{id, name, ..., parent_id}].
 *    This mode supports infinte nested rows.
 *    One row in the table is created for each child row, with identical columns as the parent row
 *    To enable this, set:
 *    - expandable = true
 *    - primaryColumn = "name" (or whatever the column property is called)
 *    - parentIdColumn = "parent_id" (or whatever the parent_id property is called)
 *    - optionally expandableIndentIcons = false if all rows does not have an icon related to the primary column.
 *
 *    Loading child rows lazily, when there are too many to read up front:
 *    - childCountColumn   Property on a row holding how many children it has, so a row can offer an
 *                         expand toggle before its children exist. Without it a row with no children
 *                         loaded has nothing to expand and can never be opened.
 *    - onExpand           Called with the item every time a row is expanded. Append the children to
 *                         `data` and they nest themselves; return the fetch promise and the row shows
 *                         a placeholder until it settles. Called on every expand, so the consumer —
 *                         which owns the data — is the one to decide whether to fetch again.
 *    - childFilter        See below. Rows that cannot be matched in the browser, because they have not
 *                         been read, are searched server-side instead.
 *
 * 2. Child components:
 *    "data" should be on the form [{id, name, ..., array_with_columns_for_child_component}]
 *    This mode supports nested data in one level, but with multiple components.
 *    One child row with one cell is rendered. The components specified in expandableComponents are rendered in this cell.
 *    To enable this, set:
 *    - expandable = true
 *    - expandableComponents = Array of objects {dataKey: "array_with_columns_for_child_component", showHeader, Component, className, columns: []}
 *                             If the Component is left out, a regular table with the data from the dataKey property is rendered.
 *
 *    Optional properties on an expandableComponents entry:
 *    - idColumn   The child rows' own id column. Defaults to the table's idColumn.
 *
 *    Children of this kind are read with their parent rather than on demand, so a row offers an
 *    expansion when it has some. Lazily loaded children are mode 1 above.
 *
 * Columns of a table that holds more than one kind of row:
 *    - appliesTo   The object_name(s) a column says anything about, as a string or an array. A row of
 *                  another kind renders an empty cell for it, rather than the "-" that a row of the
 *                  right kind shows when its value is unknown — a dataset may have an unknown row
 *                  count, while a field has no row count to be unknown. The column and its header are
 *                  unaffected; only the cells of the other kinds go blank.
 *
 * Filtering rows that have not been loaded (they cannot be matched in the browser — they are not
 * there), in either mode:
 *    - childFilter = {fetch, top}
 *                 `fetch(filterTerm, {signal, top, fields})` resolves to the matching rows from
 *                 anywhere in the table, shaped exactly as the rows in `data` are. They are added to
 *                 the table for as long as the filter is active, which is what makes a match reveal
 *                 itself: its ancestors are kept by the filter and expanded, so it appears in place,
 *                 and its unmatched siblings — never asked for — do not.
 *                 `fields` is the columns the filter's column selector currently has ticked, and the
 *                 fetch must search exactly those: a row matched on an unticked column would be
 *                 filtered straight back out again. With nothing ticked there is no request at all.
 */
export const _DataTable = (
  {
    data,
    title,
    buttons,
    hideToolbar,
    idColumn,
    parentIdColumn,
    primaryColumn = idColumn.replace('_id', '_name'),
    columns,
    rowClassName,
    selectedItems = [],
    setSelectedItems,
    editable = true,
    expandable = false,
    expandableIndentIcons = true, // Set to false if there are assets in the table both with and withot leading icon. Those with an icon cannot be expandable.
    expandableComponents, // Array of objects {dataKey, showHeader, Component, className, columns: []}
    expandedRowClassName, // Sets a className on the <tr> element for each row that is expanded
    childCountColumn, // Property holding a row's number of children, for lazily loaded child rows
    onExpand, // Called with the item when a row is expanded. Return a promise to show a loading state.
    childFilter, // {fetch, top} — server-side filtering of rows that have not been loaded
    loading = false,
    loadingMoreRows = false,
    filterable = false,
    header = true,
    filterObjects,
    filterObjectsValueMap,
    filterControls,
    tabFilter,
    filterControlsPosition = 'left', // 'left' | 'right' — which side of the filter box they sit on
    defaultFilterTerm,
    defaultSort,
    className,
    onMouseEnterRow,
    onMouseLeaveRow,
    paginated = true,
    pageSize = 200,
    currentPage = 1,
    showViewModes = false,
    viewModes,
    groupByColumn,
  },
  ref
) => {
  const [displayNames] = useGlobalState('displayNames');

  const { queryParams, setHistory } = useQueryParams();

  filterObjects =
    queryParams.filterObjects ||
    (filterObjects &&
      filterObjects.map(x => {
        const labelValue = x.split('.').pop();
        return {
          value: x,
          label:
            (displayNames[labelValue] &&
              (displayNames[labelValue]?.display_name_alternative_name &&
              labelValue !== primaryColumn
                ? displayNames[labelValue]?.display_name_alternative_name
                : displayNames[labelValue]?.display_name)) ||
            labelValue,
          tooltip: displayNames[labelValue] && displayNames[labelValue]?.display_name_description,
          checked: true,
        };
      }));

  const [filteredFilterObjects, setFilteredFilterObjects] = useState(filterObjects);

  // Rows matching the current filter that the table has not loaded. See childFilter in the leading
  // comment.
  const { childMatches, childMatchesTruncated, loadingChildMatches, requestChildMatches } =
    useChildFilter({ childFilter, defaultTop: pageSize });

  /**
   * The expandableComponents prop, normalised to an array with a Component on every entry. Built
   * once per render instead of per row, and as a copy — assigning the fallback Component onto the
   * prop itself would mutate the caller's config object.
   */
  const expandableConfigs = useMemo(
    () =>
      expandableComponents
        ? (Array.isArray(expandableComponents) ? expandableComponents : [expandableComponents]).map(
            config => ({ ...config, Component: config.Component || DataTableRow })
          )
        : [],
    [expandableComponents]
  );

  /**
   * The table's rows plus any the server-side search turned up, which is what the whole
   * filter/sort/paginate pipeline below then runs on. Adding them before the pipeline is what makes
   * them behave like any other row: the filter keeps their ancestors, the hierarchy nests them, and
   * the sort orders them among their siblings.
   *
   * A match that is already loaded is dropped rather than added twice — the hierarchy would nest the
   * same row under its parent once per copy.
   */
  const rows = useMemo(() => {
    if (!childMatches?.length || !data) return data;

    const loaded = new Set(data.map(row => row[idColumn]));

    return [...data, ...childMatches.filter(row => !loaded.has(row[idColumn]))];
  }, [data, childMatches, idColumn]);

  /**
   * Whether anything in this table is hidden in its source system.
   *
   * The "Show hidden" filter is offered only when it would do something. Nearly every asset can
   * carry a hidden flag, so asking whether the column exists offers the filter on every table in
   * the application, including a database schema where nothing is ever hidden. Asking whether a row
   * actually is hidden offers it where it means something -- which in practice is Power BI, without
   * naming Power BI anywhere.
   *
   * Read from `data` rather than from the filtered rows: the answer must not change when the filter
   * takes the hidden rows away, or the control would remove itself the moment it was used.
   */
  const hasHiddenRows = useMemo(() => (data || []).some(isRowHiddenAtSource), [data]);

  const [showHidden, setShowHidden] = useState(
    queryParams.filterControls[SHOW_HIDDEN] || localStorage.getItem(SHOW_HIDDEN) === 'true' || false
  );

  /**
   * The rows to show, with source-hidden ones removed unless the toggle is on.
   *
   * A hidden row takes its descendants with it: leaving them behind would either orphan them or
   * promote them to the root, and a child of something the source hides is not something to show on
   * its own.
   */
  const visibleRows = useMemo(() => {
    if (!hasHiddenRows || showHidden || !rows) return rows;

    const removed = new Set(rows.filter(isRowHiddenAtSource).map(row => row[idColumn]));

    if (removed.size === 0) return rows;

    // Walk down from the removed rows so a hidden grandparent takes the whole branch.
    let grew = true;
    while (grew) {
      grew = false;
      for (const row of rows) {
        if (!removed.has(row[idColumn]) && removed.has(row[parentIdColumn])) {
          removed.add(row[idColumn]);
          grew = true;
        }
      }
    }

    return rows.filter(row => !removed.has(row[idColumn]));
  }, [rows, hasHiddenRows, showHidden, idColumn, parentIdColumn]);

  /**
   * The tab filter's configuration, read out as primitives.
   *
   * A view builds the config inline, so its identity changes on every render. Everything below
   * depends on these rather than on the object: a new array of rows would rebuild the hierarchy and
   * re-render every row on the page.
   */
  const {
    column: tabFilterColumn,
    labelColumn: tabFilterLabelColumn,
    sortColumn: tabFilterSortColumn,
    name: tabFilterName = 'tab_filter',
  } = tabFilter || {};

  const [tabFilterValue, setTabFilterValue] = useState(TAB_FILTER_ALL);

  /**
   * What the view last asked to start from, and whether that has been honoured yet.
   *
   * The default cannot be read once when the state is created: a view builds the config out of data
   * it is still loading — which datasource this is, and so whether to offer the filter at all — so
   * on the first render there is usually no config to read. Held in a ref because it is read when
   * the options settle, not on every render.
   */
  const tabFilterDefault = useRef(tabFilter?.defaultValue);
  const tabFilterDefaultApplied = useRef(false);

  tabFilterDefault.current = tabFilter?.defaultValue;

  /**
   * The filter's options: All, then one per distinct value among the top-level rows, each with how
   * many of them there are.
   *
   * Only the top level is counted, because that is what the filter selects — the rows nested under
   * a match come along with it whatever kind they are. Options are derived from the rows present,
   * so every button leads somewhere and none needs disabling.
   */
  const tabFilterOptions = useMemo(() => {
    if (!tabFilterColumn || !visibleRows) return undefined;

    const values = new Map();

    for (const row of visibleRows) {
      if (row[parentIdColumn] != null) continue;

      const value = row[tabFilterColumn];

      if (value == null) continue;

      const option = values.get(value);
      // Where several rows share a value the earliest sort order wins, so a column that groups rows
      // -- a type category, standing for the several types within it -- puts its button no further
      // along than the first type it stands for would have sat.
      const sortOrder = tabFilterSortColumn ? row[tabFilterSortColumn] : value;

      if (option) {
        option.count += 1;
        if (compareItems({ sortOrder }, option, { key: 'sortOrder', direction: 'asc' }) < 0) {
          option.sortOrder = sortOrder;
        }
      } else
        values.set(value, {
          value,
          // Without a column to read the label from, the value is the label -- formatted, since a
          // column of codes reads as code otherwise, and plural, because the button stands for
          // every row of that kind rather than for one of them. Always plural, whatever the count
          // beside it says: the label names the kind, and a button reading "Report 1" next to
          // "Dashboards 4" would look like a different sort of thing.
          label: tabFilterLabelColumn
            ? row[tabFilterLabelColumn]
            : pluralize(formatUserFriendly(value), 2),
          sortOrder,
          count: 1,
        });
    }

    if (values.size === 0) return undefined;

    // The table's own comparator, so a sort column that is a number, a name or absent orders the
    // same way here as it would in a column.
    const options = [...values.values()].sort((a, b) =>
      compareItems(a, b, { key: 'sortOrder', direction: 'asc' })
    );

    return [
      {
        value: TAB_FILTER_ALL,
        label: 'All',
        secondaryLabel: options.reduce((total, x) => total + x.count, 0).toLocaleString(),
      },
      ...options.map(x => ({
        value: x.value,
        label: x.label,
        secondaryLabel: x.count.toLocaleString(),
      })),
    ];
  }, [visibleRows, tabFilterColumn, tabFilterLabelColumn, tabFilterSortColumn, parentIdColumn]);

  // The options as a value that changes only when the set of them does. The rows are replaced
  // whenever anything about them is refreshed, which rebuilds the options without changing what
  // they say.
  const tabFilterOptionKey = (tabFilterOptions || []).map(x => x.value).join(',');

  /**
   * Settles the selection whenever the options change — once the rows first arrive, and again when
   * the view navigates to another set of them without remounting the table.
   *
   * The first time there are options to choose from, the view's default wins: that is where a
   * selection remembered in the URL comes back from, after a reload or a return to the page. From
   * then on the selection is the user's. Either way a value the rows have none of falls back to All
   * rather than filtering every row away.
   */
  useEffect(() => {
    if (!tabFilterOptions) return;

    const isOffered = value => value != null && tabFilterOptions.some(x => x.value === value);

    setTabFilterValue(current => {
      if (!tabFilterDefaultApplied.current) {
        tabFilterDefaultApplied.current = true;

        if (isOffered(tabFilterDefault.current)) return tabFilterDefault.current;
      }

      return isOffered(current) ? current : TAB_FILTER_ALL;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFilterOptionKey]);

  /**
   * Applies the tab filter: the top-level rows of the selected value, and everything nested under
   * them.
   *
   * Only the top level is matched. A row whose parent is not kept would be dropped rather than
   * promoted to the root, so a subtree has to be kept whole — the same descent the hidden-row
   * filter above does, with the opposite predicate.
   */
  const tabFilteredRows = useMemo(() => {
    if (!tabFilterColumn || !visibleRows || tabFilterValue === TAB_FILTER_ALL) return visibleRows;

    const kept = new Set(
      visibleRows
        .filter(row => row[parentIdColumn] == null && row[tabFilterColumn] === tabFilterValue)
        .map(row => row[idColumn])
    );

    // Nothing matched — the value does not belong to these rows, and the effect above is about to
    // reset it. Showing everything for that one render beats showing nothing.
    if (kept.size === 0) return visibleRows;

    let grew = true;
    while (grew) {
      grew = false;
      for (const row of visibleRows) {
        if (!kept.has(row[idColumn]) && kept.has(row[parentIdColumn])) {
          kept.add(row[idColumn]);
          grew = true;
        }
      }
    }

    return visibleRows.filter(row => kept.has(row[idColumn]));
  }, [visibleRows, tabFilterColumn, tabFilterValue, idColumn, parentIdColumn]);

  /**
   * Selects a tab filter option. The value is kept here so the rows can be filtered by it; the view
   * is told so it can put it in the URL, which is its to own — a table cannot know which parameter
   * a page keeps its selection in.
   * @param {object} res - `{ [name]: value }` from the control
   * @returns {void}
   */
  const handleTabFilterChange = res => {
    const value = res[tabFilterName];

    setTabFilterValue(value);
    tabFilter?.onChange && tabFilter.onChange(value);
  };

  const { filteredItems, requestFilter, filterTerm } = useFilterableData(
    tabFilteredRows,
    filteredFilterObjects,
    filterObjectsValueMap,
    defaultFilterTerm,
    idColumn,
    parentIdColumn
  );
  const { sortedItems, requestSort, sortConfig } = useSortableData(filteredItems, defaultSort);
  const { items, isHierarchy } = useHierarchialData(sortedItems, idColumn, parentIdColumn);

  const [page, setPage] = useState(queryParams.tablePage || currentPage);
  const [scroll, setScroll] = useState(queryParams.tableScroll);
  const [expandedItems, setExpandedItems] = useState(queryParams.expandedItems || []);
  const [expandedItemsHistory, setExpandedItemsHistory] = useState(queryParams.expandedItems || []); // Used to save and restore previous expanded state when filtering
  const [viewMode, setViewMode] = useState(
    queryParams.tableViewMode || localStorage.getItem('tableViewMode') || 'statistics'
  );
  const [allSelected, setAllSelected] = useState();
  const [_viewModes] = useState(
    showViewModes
      ? viewModes || [
          {
            icon: 'statistics',
            value: 'statistics',
            tooltip: 'Toggle statistics columns',
          },
          {
            icon: 'descriptions',
            value: 'descriptions',
            tooltip: 'Toggle description column',
          },
        ]
      : undefined
  );

  // What the expand handler below needs, refreshed after each commit. They cannot be its
  // dependencies: it is a prop of every row, and a handler rebuilt on each change of what is open
  // re-renders every row on the page with it.
  const expandedItemsRef = useRef({});

  // Children read when a row is opened, and the rows left open by a reload read again.
  const { loadingItems, loadChildren } = useLazyChildren({
    childCountColumn,
    onExpand,
    rows,
    idColumn,
    parentIdColumn,
    expandedItems,
    filterTerm,
  });

  useEffect(() => {
    expandedItemsRef.current = { expandedItems, loadChildren };
  });

  const { showTooltip, hideTooltip } = useTooltip();

  const tmpRef = useRef();
  const wrapperRef = ref || tmpRef;

  /**
   * Reports whether a row is expanded.
   * @param {object} item Row item
   * @returns {boolean} True when the row's id is in the expanded set
   */
  const isExpanded = item => expandedItems.some(x => x === item[idColumn]);

  useEffect(() => {
    const element = wrapperRef.current;

    if (element) {
      element.addEventListener('scroll', trackScrolling);
      return () => {
        element.removeEventListener('scroll', trackScrolling);
      };
    }
  }, [wrapperRef.current]);

  useLayoutEffect(() => {
    // Restore scroll
    wrapperRef.current.scrollTop = scroll;
  }, [wrapperRef.current, items]);

  useEffect(() => {
    // Expand items if expandable. A match keeps its ancestors (useFilterableData), so expanding
    // every row that survived the filter is what reveals the matching descendants — including the
    // ones the server-side search added, since those are rows of the table like any other.
    if (expandable && parentIdColumn) {
      if ((filterTerm || '').length > 1) {
        setExpandedItems(filteredItems.map(x => x[idColumn]));
      } else {
        setExpandedItems(expandedItemsHistory || []);
      }
    }
  }, [filteredItems]);

  useEffect(() => {
    // A filter term restored from the URL never goes through handleFilter, so start the server-side
    // child search for it once, on mount.
    if (childFilter && (filterTerm || '').length > 1) {
      requestChildMatches(filterTerm, childFilterFields());
    }
  }, []);

  useLayoutEffect(() => {
    data && setAllSelected(data.length === selectedItems.length);
  }, [data, selectedItems]);

  const trackScrolling = useDebounce(() => {
    // Debounce to reduce number of calls to setState
    setScroll(wrapperRef.current.scrollTop);
    setHistory({ tableScroll: wrapperRef.current.scrollTop });
  }, 200).debounce;

  /**
   * Records which rows are open, so they come back when the page is returned to or reloaded.
   *
   * Debounced because writing it navigates, and navigating renders the table a second time — which
   * doubles the cost of opening a row, over a table where every row renders. Nothing on screen waits
   * for it: expandedItems is what the rows are drawn from, and this only has to be in place by the
   * time the page is left.
   */
  const saveExpandedToHistory = useDebounce(expanded => {
    setHistory({ expandedItems: expanded });
  }, 200).debounce;

  /**
   * The columns the filter's column selector currently has ticked.
   *
   * Takes the selector state as an argument because the handler that changes it has to ask about the
   * array it has just built, which is not state yet in that tick.
   *
   * @param {object[]} [objects] Selector entries; defaults to the current ones
   * @returns {string[]} Ticked column names
   */
  const childFilterFields = (objects = filteredFilterObjects) =>
    (objects || []).filter(x => x.checked).map(x => x.value);

  const handleFilter = str => {
    //, value) => {
    // Go to first page and scroll to top
    selectPage(1);

    // De-select any selected items
    setSelectedItems && setSelectedItems([]);

    // Search the lazily loaded children server-side. A term of one character or less is treated as
    // no filter, exactly as the in-browser filter treats it.
    if (childFilter) {
      requestChildMatches(str, childFilterFields());
    }

    // Apply filter
    requestFilter(str); //, value)
  };

  const handleFilterObjectsFilter = item => {
    let array = filteredFilterObjects.map(x => {
      let checked;
      if (Array.isArray(item)) {
        let value = item.find(y => y.value === x.value);
        checked = value !== undefined && value.checked;
      } else {
        checked = x.value === item.value ? !x.checked : x.checked;
      }

      return {
        ...x,
        checked: checked,
      };
    });

    setFilteredFilterObjects(array);
    requestFilter(filterTerm);

    // The selector decides which child columns the server searches as well, so a change to it
    // re-runs the child search — with the columns just ticked, or clearing it when the last child
    // column was unticked.
    if (childFilter) {
      requestChildMatches(filterTerm, childFilterFields(array));
    }

    setHistory({ filterObjects: array });
  };

  const handleSort = value => {
    // Go to first page and scroll to top
    selectPage(1);

    // Apply sort
    requestSort(value, groupByColumn);
  };

  const handleViewModeChange = event => {
    const mode = event.value;
    setViewMode(mode);
    setHistory({ tableViewMode: mode });

    // Persist state in local storage
    localStorage.setItem('tableViewMode', mode);
  };

  const resetScroll = () => {
    wrapperRef.current.scrollTop = 0;
    setHistory({ tableScroll: 0 });
  };

  const getClassNamesFor = name => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  /**
   * Reports whether any row populates the given field. Used to hide the header slot and sort
   * affordance for inline entries that no row renders (e.g. a field-level icon on a dataset row).
   * @param {string} field Data field backing an inline entry
   * @returns {boolean} True when at least one row has a value for the field
   */
  const inlineFieldHasData = useCallback(field => data?.some(row => row[field] != null), [data]);

  // The rows and the setter the selection works against, refreshed after each commit. They cannot
  // be dependencies of the handler below: it is a dependency of every row's memo, and a handler
  // rebuilt on each data change rebuilds every row on the page with it.
  const selectionInputs = useRef({});
  useEffect(() => {
    selectionInputs.current = { rows, setSelectedItems };
  });

  /**
   * Adds rows to the selection and removes rows from it.
   *
   * Built from the previous selection rather than from a captured copy, which is what lets the
   * handler keep one identity for the table's lifetime: it never has to close over the selection.
   *
   * @param {Array<{objectName: string, objectId: string|number, checked: boolean}>} itemsToSelect
   */
  const selectItems = useCallback(
    itemsToSelect => {
      const { rows: currentRows, setSelectedItems: setItems } = selectionInputs.current;

      setItems(prev => {
        let tmp = [...prev];

        itemsToSelect.forEach(itemToSelect => {
          const itemToSelectKey = itemToSelect.objectId + (itemToSelect.objectName || '');

          if (itemToSelect.checked) {
            if (tmp.findIndex(x => selectionKey(x, idColumn) === itemToSelectKey) === -1) {
              // add only if not already in array
              const row = (currentRows || []).find(
                x => selectionKey(x, idColumn) === itemToSelectKey
              );
              row && tmp.push(row);
            }
          } else {
            tmp = tmp.filter(x => selectionKey(x, idColumn) !== itemToSelectKey);
          }
        });

        return tmp;
      });
    },
    [idColumn]
  );

  const handleSelect = useCallback(
    event => {
      const { objectName, objectId, checked } = event;

      // Add item to selection array
      selectItems([{ objectName, objectId, checked }]);
    },
    [selectItems]
  );

  const handleSelectAll = event => {
    event.preventDefault();

    const checked = !allSelected;

    let array = [];

    filteredItems.forEach(item => {
      let objectId = item[idColumn].toString();
      let objectName = item?.object_name;
      array.push({ objectName, objectId, checked });
    });

    // Add items to selection array
    selectItems(array);
  };

  const handleCollapseAll = (event, expandedItems = []) => {
    setExpandedItems([]);
    setExpandedItemsHistory([]);
    saveExpandedToHistory([]);
  };

  /**
   * Expands or collapses a row, and asks the consumer to load its children the first time it opens.
   *
   * The onExpand call is deliberately outside the state updater: an updater must stay pure, and
   * React invokes it twice in development, which would fire two requests per expansion.
   *
   * @param {string|number} itemId Value of the row's idColumn
   * @param {object} [item] The row item, passed on to onExpand
   */
  const handleExpand = useCallback((itemId, item) => {
    const open = expandedItemsRef.current.expandedItems;
    const willExpand = !open.some(x => x === itemId);
    const tmp = willExpand ? [...open, itemId] : open.filter(x => x !== itemId);

    setExpandedItems(tmp);
    setExpandedItemsHistory(tmp);
    saveExpandedToHistory(tmp);

    if (!willExpand || !item) return;

    expandedItemsRef.current.loadChildren(itemId, item);
  }, []);

  const itemCount = items.length;
  // The rows only ask whether the table has any rows at all; the count itself would change whenever
  // one is added and re-render every one of them.
  const hasRows = itemCount > 0;
  const selectedItemsCount = selectedItems.length;

  const rowActionEdit = buttons && buttons.find(x => x.action === 'edit')?.onClick;
  const rowActionDelete = buttons && buttons.find(x => x.action === 'delete')?.onClick;
  const rowActionRemove = buttons && buttons.find(x => x.action === 'remove')?.onClick;
  const rowActionRun = buttons && buttons.find(x => x.action === 'run');
  const rowActionAbort = buttons && buttons.find(x => x.action === 'abort');

  // Run and abort share one column slot — they are mutually exclusive per row.
  const rowActionCount =
    (rowActionEdit ? 1 : 0) +
    (rowActionDelete ? 1 : 0) +
    (rowActionRemove ? 1 : 0) +
    (rowActionRun || rowActionAbort ? 1 : 0);

  const paginatedItems = paginated ? items.slice((page - 1) * pageSize, page * pageSize) : items;
  const pageCount = Math.ceil(items.length / pageSize);

  const stepPage = steps => {
    if (steps < 0) {
      if (page + steps < 1) {
        selectPage(1);
      } else {
        selectPage(page + steps);
      }
    } else {
      if (page + steps > pageCount) {
        selectPage(pageCount);
      } else {
        selectPage(page + steps);
      }
    }
  };

  const selectPage = pageNum => {
    resetScroll();
    setPage(pageNum);
    setHistory({ tablePage: page });
  };

  const pages = [];
  let i = 0;
  while (i < pageCount) {
    pages.push(i + 1);
    i++;
  }

  let columnCount = editable ? 2 : 0;
  columns.forEach(column => {
    columnCount = columnCount + (column.type === 'chart' ? 2 : 1);
  });

  /**
   * Orders the child rows of an expansion. They arrive already ordered — from the endpoint that
   * loaded them — so the active sort is only applied when the sorted field exists on the child rows
   * as well, which is when the user's intent plainly covers them (sorting on a description sorts
   * the datasets and the fields under them alike). Sorting on a field the children do not have
   * would otherwise silently shuffle them.
   *
   * @param {object[]} [childRows] Child rows for one expandableComponents entry
   * @returns {object[]} The rows, sorted when the active sort applies to them
   */
  const sortChildRows = childRows => {
    if (!childRows || childRows.length === 0) return [];
    if (!sortConfig?.key || childRows[0][sortConfig.key] === undefined) return childRows;

    return [...childRows].sort((a, b) => compareItems(a, b, sortConfig));
  };

  const renderRows = (items, depth = 0) => {
    let rows = [];
    let groupIndex = 1;
    let prevRowGroup;

    (items || paginatedItems).forEach((item, index) => {
      groupIndex = item[groupByColumn] && item[groupByColumn] === prevRowGroup ? groupIndex + 1 : 1;
      prevRowGroup = item[groupByColumn];

      const selected = selectedItems.find(x => {
        return selectionKey(x, idColumn) === selectionKey(item, idColumn);
      });
      const expanded = isExpanded(item);
      const isExpandable = expandable;

      // How many children the row has across its expansions, which is what decides whether it offers
      // one at all.
      const childCount = expandableConfigs.reduce(
        (count, config) => count + (item[config.dataKey]?.length || 0),
        0
      );

      // How many child rows the row has, for a hierarchy whose children are loaded on demand: a row
      // with none loaded has nothing to count, so the count comes from the row itself.
      const lazyChildCount = childCountColumn ? item[childCountColumn] || 0 : 0;

      const canExpand =
        (item.children && item.children.length > 0) || childCount > 0 || lazyChildCount > 0;

      rows.push(
        <DataTableRow
          key={(item[idColumn] || '') + '-' + depth + '-' + index}
          depth={depth}
          className={
            (depth > 0 && 'cell-expansion') +
            (index + 1 === (items || paginatedItems).length && depth === 1
              ? ' last-row-in-expansion'
              : '') +
            (rowClassName ? ' ' + item[rowClassName] : '')
          }
          item={item}
          sourceHidden={isRowHiddenAtSource(item)}
          hasRows={hasRows}
          idColumn={idColumn}
          parentIdColumn={parentIdColumn}
          primaryColumn={primaryColumn}
          groupIndex={groupIndex}
          columns={columns}
          inlineFieldHasData={inlineFieldHasData}
          selected={selected}
          editable={editable}
          onSelect={handleSelect}
          actionEdit={rowActionEdit}
          actionDelete={rowActionDelete}
          actionRemove={rowActionRemove}
          actionRun={rowActionRun}
          actionAbort={rowActionAbort}
          onMouseEnterRow={onMouseEnterRow}
          onMouseLeaveRow={onMouseLeaveRow}
          // childCountColumn included because isHierarchy only reports what the data already shows:
          // a table whose child rows are all still to be loaded holds no parent yet, and would
          // otherwise offer no way to load them.
          expandable={
            isExpandable && (isHierarchy || expandableConfigs.length > 0 || !!childCountColumn)
          }
          expandableIndentIcons={expandableIndentIcons}
          canExpand={canExpand}
          childCount={childCount || lazyChildCount}
          expanded={expanded}
          onExpand={handleExpand}
          viewModes={_viewModes}
          viewMode={viewMode}
        />
      );

      const loadingChildren = loadingItems.some(x => x === item[idColumn]);

      // A row whose child rows are on their way: a placeholder in their place, indented where they
      // will appear, so an expanded row never looks as though it opened onto nothing. Only for a
      // hierarchy that loads its children on demand — an expansion of its own has its own.
      if (childCountColumn && expanded && loadingChildren && !item.children?.length) {
        rows.push(
          <tr key={depth + '-' + index + '-loading'} className="row cell-expansion">
            {editable && <td></td>}
            {columns.map((column, columnIndex) => (
              <td
                key={columnIndex}
                colSpan={column.type === 'chart' ? 2 : 1}
                className={
                  _viewModes && column.viewMode && column.viewMode !== viewMode
                    ? 'hide-column'
                    : undefined
                }
                style={
                  column.id === primaryColumn ? { paddingLeft: (depth + 1) * 30 + 'px' } : undefined
                }
              >
                {column.id === primaryColumn || column.type === 'description' ? (
                  <SkeletonLoader width="5vw" />
                ) : null}
              </td>
            ))}
            {editable && <td className={'last-column width-' + rowActionCount.toString()}></td>}
          </tr>
        );
      }

      // Render child row with custom components. Before any hierarchical child rows, so a row that
      // has both keeps its own children next to it rather than below its whole subtree. A row with
      // nothing to put in an expansion gets none: in a tree where only some rows have children of
      // this kind, every other expanded row would otherwise carry an empty one.
      if (expandable && expanded && (childCount > 0 || loadingChildren)) {
        rows.push(
          <tr
            key={depth + '-' + index + '-expanded'}
            className={'row-expansion ' + (expandedRowClassName || '')}
          >
            <td
              colSpan={columnCount}
              className="cell-expansion"
              // Indented one level below the row it belongs to, on the same 30px step the tree's own
              // rows use, so an expansion under a nested row lines up with that row's children
              // rather than with the top level. The 10px is the cell's own left margin, which is
              // what makes a depth-0 expansion land on the 40px the stylesheet has always used.
              style={{ paddingLeft: (depth + 1) * 30 + 10 + 'px' }}
            >
              {expandableConfigs.map((expandableComponent, configIndex) => {
                const childRows = sortChildRows(item[expandableComponent.dataKey]);

                // The children are still on their way. Which columns they will fill is known from
                // the config, so the placeholder can be the shape of the real thing.
                if (loadingChildren && childRows.length === 0) {
                  return (
                    <table key={configIndex} className="hide-header">
                      <tbody>
                        <tr className="row">
                          {expandableComponent.columns.map((column, index) => (
                            <td
                              key={index}
                              className={
                                _viewModes && column.viewMode && column.viewMode !== viewMode
                                  ? 'hide-column'
                                  : undefined
                              }
                            >
                              <SkeletonLoader width={column.type === 'icon' ? '1em' : '5vw'} />
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  );
                }

                if (childRows.length === 0) return null;

                return (
                  <table
                    key={configIndex}
                    className={
                      (expandableComponent.showHeader ? '' : 'hide-header') +
                      (expandableComponent.className ? ' ' + expandableComponent.className : '')
                    }
                  >
                    <thead>
                      <tr>
                        {expandableComponent.columns.map((column, index) => (
                          <th
                            key={index}
                            className={
                              _viewModes && column.viewMode && column.viewMode !== viewMode
                                ? 'hide-column'
                                : undefined
                            }
                          >
                            {column.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {childRows.map((childRow, childIndex) => {
                        return (
                          <expandableComponent.Component
                            key={childIndex}
                            item={childRow}
                            hasRows={childRows.length > 0}
                            // A child row sits in its own table, indented by that table's CSS, so
                            // it starts over at depth 0 rather than inheriting the parent's indent.
                            depth={0}
                            idColumn={expandableComponent.idColumn || idColumn}
                            primaryColumn={expandableComponent.primaryColumn}
                            columns={expandableComponent.columns}
                            // Scoped to the child rows: whether the parent rows happen to carry a
                            // field says nothing about whether the children render it.
                            inlineFieldHasData={field => childRows.some(row => row[field] != null)}
                            editable={false}
                            expandable={expandableComponent.expandable}
                            onExpand={handleExpand}
                            // The toolbar's view-mode toggle governs the child columns too: a child
                            // column carrying a viewMode is shown and hidden with the parent ones.
                            viewModes={_viewModes}
                            viewMode={viewMode}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                );
              })}
            </td>
          </tr>
        );
      }

      // Render child rows if hierarchical data
      if (parentIdColumn && item.children && item.children.length > 0 && expanded) {
        rows = [...rows, ...renderRows(item.children, depth + 1)];
      }
    });

    return rows;
  };

  /* TODO: Add support for resizing column widths
  const tableRef = useRef(null);
  const [colWidths, setColWidths] = useState([]);

  useEffect(() => {
    if (tableRef.current && colWidths.length === 0) {
      const widths = Array.from(tableRef.current.querySelectorAll('th')).map(th => th.offsetWidth);
      setColWidths(widths);
    }
  }, [tableRef.current]);

  const startResizing = (index, e) => {
    const startX = e.clientX;
    const startWidth = colWidths[index];

    const onMouseMove = (e) => {
      const newWidth = startWidth + (e.clientX - startX);
      setColWidths((widths) => {
        const updated = [...widths];
        updated[index] = Math.max(newWidth, 50);
        return updated;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
*/
  let savedPrimaryColumn;

  /**
   * The filter controls the toolbar shows: the ones the view asked for, plus this table's own
   * "Show hidden" when its rows can be hidden at the source. Memoized so the toolbar is not handed
   * a new array on every render.
   */
  const toolbarFilterControls = useMemo(() => {
    if (!hasHiddenRows) return filterControls;

    return [
      ...(filterControls || []),
      {
        type: 'checkbox',
        name: SHOW_HIDDEN,
        label: 'Show hidden',
        tooltip: 'Show assets that are hidden in the source system',
        defaultValue: showHidden,
        saveInLocalStorage: true,
        onChange: ({ [SHOW_HIDDEN]: value }) => setShowHidden(value),
      },
    ];
  }, [filterControls, hasHiddenRows, showHidden]);

  return (
    <React.Fragment>
      {/* Its own row above the toolbar, rather than a control inside it: a row of buttons with
          counts needs the width, and it governs everything below it — the toolbar's own filter and
          counts included. Shown only where there is something to choose between, since one kind
          and All is not a choice. */}
      {tabFilterOptions && tabFilterOptions.length > 2 && (
        <CustomFilterControl
          type="select-buttons"
          name={tabFilterName}
          className="data-table-filter-row"
          design="tab-button"
          tooltip={tabFilter?.tooltip}
          options={tabFilterOptions}
          value={tabFilterValue}
          onChange={handleTabFilterChange}
        />
      )}

      {!hideToolbar && (
        <DataTableToolbar
          title={title}
          buttons={buttons}
          // The rows the view handed the table. Chosen over rows.length deliberately: no total here
          // can be the whole truth, since lazy-loaded children that have never been loaded are not known to the
          // table at all, and "what this list was given" is the one definition that holds in every
          // state.
          //
          // ⚠️ The anomaly that comes with that choice: while a filter is active, the table uses lazy loading of children and a childFilter can
          // fetch matching rows the table never held. They are counted by itemCount below but not
          // here, so "Showing x of y" can read an x larger than y. rows.length would make the two
          // agree without being a true total either, and it also decides whether a full selection
          // reads "All n assets selected" — so this stays as it is.
          itemTotalCount={data?.length}
          // The rows a filter leaves — matches plus the ancestors kept with them — which is what
          // "Showing x of y" counts. Not items.length: that counts the tree's roots.
          itemCount={filteredItems.length}
          selectedItems={selectedItems}
          selectedItemsCount={selectedItemsCount}
          hasRows={hasRows}
          editable={editable}
          filterable={filterable}
          requestFilter={handleFilter}
          onFilterObjectsFilter={handleFilterObjectsFilter}
          filterObjects={filteredFilterObjects}
          filterControls={toolbarFilterControls}
          filterControlsPosition={filterControlsPosition}
          filterTerm={filterTerm}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          viewModes={_viewModes}
        />
      )}

      <div className="DataTableWrapper" ref={wrapperRef}>
        <table
          className={
            'DataTable' + (className ? ' ' + className : '') + (!header ? ' no-header' : '')
          } /*ref={tableRef}*/
        >
          {header && (
            <thead>
              <tr>
                {
                  // Render select-all-rows checkbox
                  editable && (
                    <th className="checkbox" onClick={e => handleSelectAll(e)}>
                      <div className="th-container">
                        {items.length > 0 && (
                          <Checkbox
                            name={`select-all`}
                            onChange={e => handleSelectAll(e)}
                            checked={allSelected}
                          />
                        )}
                      </div>
                    </th>
                  )
                }

                {
                  // Render columns
                  columns.map((column, index) => {
                    let isPrimaryColumn = false;

                    if (column.id === primaryColumn && !savedPrimaryColumn) {
                      isPrimaryColumn = true;
                      savedPrimaryColumn = column.id;
                    }

                    return (
                      <th
                        key={index}
                        className={
                          (column.className ? column.className : '') +
                          (column.type === 'icon' ? ' type-icon-header' : '') +
                          (column.align === undefined ? ' left' : ' ' + column.align) +
                          (_viewModes && column.viewMode && column.viewMode !== viewMode
                            ? ' hide-column'
                            : '')
                        }
                        colSpan={column.type === 'chart' ? 2 : 1}
                      >
                        <div className="th-container">
                          {
                            // Render collapse-all button if expandable
                            expandable && // is expandable
                              isPrimaryColumn && // column is primary column
                              // row has children, an expansion of its own, or children still to load
                              (isHierarchy || expandableConfigs.length > 0 || !!childCountColumn) &&
                              // Render the collapse-all toggle slot whenever the body reserves a
                              // matching expand-toggle slot: in indent-icon mode every row reserves
                              // one, otherwise only once something is expanded or there are no
                              // inline icons occupying the primary column.
                              (expandableConfigs.length > 0 ||
                                expandableIndentIcons ||
                                expandedItems.length > 0 ||
                                !column.inline) && (
                                <div
                                  className="toggle-expand"
                                  onClick={handleCollapseAll}
                                  onMouseEnter={() => showTooltip({ title: 'Collapse all' })}
                                  onMouseLeave={hideTooltip}
                                >
                                  {expandedItems.length > 0 && (
                                    <div className="button-toggle-collapse-all">
                                      <div className="arrow-1"></div>
                                      <div className="arrow-2"></div>
                                    </div>
                                  )}
                                </div>
                              )
                          }

                          {
                            // Render a sortable mini-header per inline entry so the inline
                            // icons/pills can be sorted on, exactly as if each were its own column.
                            // The empty div also acts as a label-alignment spacer.
                            column.inline &&
                              column.inline.map((entry, index) => {
                                // Pill and text entries have no `icon` field — fall back to their
                                // value field so the sort key and tooltip resolve to a data field.
                                const sortField = entry.icon || entry.value;
                                // A view can opt an inline entry out of the header (entry.sortable
                                // === false): the body still renders its icon/pill/text, but it gets
                                // no header spacer/sort control. Useful when entries are mutually
                                // exclusive per row, when only one header slot is wanted, or when the
                                // entry belongs to a kind of row that only some of the tree holds.
                                if (entry.sortable === false) {
                                  return null;
                                }
                                // Skip entries no row populates: no body icon renders for them,
                                // so they get neither a header spacer nor a sort affordance.
                                if (!inlineFieldHasData(sortField)) {
                                  return null;
                                } else if (
                                  index === 0 &&
                                  !expandableIndentIcons &&
                                  expandedItems.length > 0
                                ) {
                                  return;
                                } else {
                                  return (
                                    <div
                                      key={index}
                                      className={
                                        'th-label sortable ' +
                                        getClassNamesFor(sortField) +
                                        (entry.className ? ' ' + entry.className : '')
                                      }
                                      onClick={() => handleSort(sortField)}
                                      onMouseEnter={() =>
                                        displayNames[sortField] &&
                                        showTooltip({
                                          title: displayNames[sortField].display_name_description,
                                        })
                                      }
                                      onMouseLeave={hideTooltip}
                                    ></div>
                                  );
                                }
                              })
                          }

                          <div
                            className={
                              'th-label sortable ' +
                              // Sort on column.sortField when the displayed value isn't itself the
                              // sort key (e.g. a column rendering a precomputed node); falls back
                              // to column.id, so untouched columns behave exactly as before.
                              getClassNamesFor(column.sortField || column.id) +
                              ' ' +
                              (isPrimaryColumn ? ' primary-column-label' : '')
                            }
                            onClick={() => handleSort(column.sortField || column.id)}
                            onMouseEnter={() =>
                              displayNames[column.id] &&
                              showTooltip({
                                title: displayNames[column.id].display_name_description,
                              })
                            }
                            onMouseLeave={hideTooltip}
                          >
                            {column.name}
                          </div>
                        </div>
                      </th>
                    );
                  })
                }

                {
                  // Render column with edit/delete controls
                  editable && <th className={'last-column width-' + rowActionCount.toString()}></th>
                }
              </tr>
            </thead>
          )}
          <tbody>
            {!loading && renderRows()}

            {
              // The server stopped at the requested number of matches, so the rows above are not
              // the whole answer.
              !loading && childMatchesTruncated && (
                <tr className="row">
                  <td colSpan={columnCount} className="empty-text small-text">
                    {`Only the first ${
                      childFilter.top || pageSize
                    } matching rows are shown. Narrow the filter to see the rest.`}
                  </td>
                </tr>
              )
            }

            {(loading || loadingMoreRows || loadingChildMatches) && (
              <tr>
                {editable && <td></td>}
                {columns.map((column, index) => (
                  <td
                    key={index}
                    colSpan={column.type === 'chart' ? 2 : 1}
                    className={
                      _viewModes && column.viewMode && column.viewMode !== viewMode
                        ? ' hide-column'
                        : ''
                    }
                  >
                    {column.type === 'icon' || column.type === 'owner' ? (
                      <SkeletonLoader width="1em" key={index} />
                    ) : (
                      <SkeletonLoader width="5vw" key={index} />
                    )}
                  </td>
                ))}
                {editable && <td className={'last-column width-' + rowActionCount.toString()}></td>}
              </tr>
            )}
          </tbody>
        </table>
        {
          // Not while a child search is in flight: its matches are what decides whether the table
          // is empty, and saying "No assets" until they land would flash on every keystroke.
          !loading && !loadingChildMatches && items.length === 0 ? (
            <div className="empty-text">No assets</div>
          ) : null
        }
      </div>
      {!loading && paginated && pageCount > 1 && (
        <DataTablePagination
          items={items}
          primaryColumn={primaryColumn}
          pages={pages}
          pageSize={pageSize}
          currentPage={page}
          stepPage={stepPage}
          selectPage={selectPage}
        />
      )}
    </React.Fragment>
  );
};

export const DataTable = forwardRef(_DataTable);
